import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormMode } from 'common/constants/enums';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { IPageFormV2 } from '../../../page-builder-v2.types';
import { buildPageUrl } from '../../../utils/build-page-url';
import { AuthorSelect } from './author-select';
import { BrandSelect } from './brand-select';
import { KeywordInput } from './keyword-input';
// import { MetadataAlerts } from './metadata-alerts';
import { PublisherSelect } from './publisher-select';
// import { useMetadata } from './use-metadata';

export interface IMetadataSectionProps {
  formMode: FormMode;
}

export const MetadataSection = ({ formMode }: IMetadataSectionProps) => {
  const { control, register } = useFormContext<IPageFormV2>();
  const [copied, setCopied] = useState(false);

  const publisher = useWatch({ control, name: 'publisher' });
  const brand = useWatch({ control, name: 'brand' });
  const slug = useWatch({ control, name: 'slug' });

  const pageUrl = buildPageUrl({
    brandName: brand?.name,
    domain: publisher?.domain,
    reverseProxyPath: publisher?.reverse_proxy_path,
    slug,
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // const [encryptBrand, setEncryptBrand] = useState(false);

 //  const { brand, publisher, slug } = useMetadata();

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { margin: '12px 0' },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
          '&.Mui-expanded': { minHeight: 52 },
          minHeight: 52,
          px: 2.5,
        }}
      >
        <Typography fontWeight={600} variant="subtitle1">Basic Information</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>

      {/* Publisher / Brand / Author */}
      <Grid container mb={2.5} spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <PublisherSelect disabled={formMode === FormMode.UPDATE} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <BrandSelect disabled={formMode === FormMode.UPDATE} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <AuthorSelect />
        </Grid>
      </Grid>

      {/* Title */}
      <Grid container mb={2.5} spacing={2}>
        <Grid size={{ xs: 12 }}>
          <KeywordInput formMode={formMode} />
        </Grid>
      </Grid>

      {/* Page URL */}
      {!!pageUrl && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Page URL"
            size="small"
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                      <IconButton edge="end" onClick={handleCopy} size="small">
                        <ContentCopyIcon sx={{ color: copied ? 'success.main' : 'text.disabled', fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    {formMode === FormMode.UPDATE && (
                      <Tooltip title="Open in new tab">
                        <IconButton
                          component="a"
                          edge="end"
                          href={pageUrl}
                          rel="noopener noreferrer"
                          size="small"
                          target="_blank"
                        >
                          <OpenInNewIcon sx={{ color: 'text.disabled', fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              },
            }}
            value={pageUrl}
          />
        </Box>
      )}

      {/* Subtitle */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField {...register('subtitle')} fullWidth label="Subtitle (Optional)" size="small" />
        </Grid>
      </Grid>

      </AccordionDetails>
    </Accordion>
  );
};
